/** Cosmetic particle trails that follow projectiles and jumps. Purely visual. */

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  shape: 'dot' | 'square' | 'spark' | 'puff';
  gravity: number;
}

export class ParticleSystem {
  particles: Particle[] = [];

  spawnTrail(trailId: string, x: number, y: number, vx: number, vy: number, rng: () => number): void {
    switch (trailId) {
      case 'trail.dust':
        this.push(x, y, (rng() - 0.5) * 0.6, (rng() - 0.5) * 0.6 - 0.2, 28, 3 + rng() * 3, 'rgba(170,135,95,0.55)', 'dot', 0);
        break;
      case 'trail.sparks':
        for (let i = 0; i < 2; i++) {
          this.push(x, y, -vx * 0.15 + (rng() - 0.5) * 2, -vy * 0.15 + (rng() - 0.5) * 2, 18, 1.5, rng() < 0.5 ? '#ffd166' : '#ff8a2b', 'spark', 0.08);
        }
        break;
      case 'trail.rainbow': {
        const hue = (performance.now() / 4) % 360;
        this.push(x, y, 0, 0, 36, 4, `hsl(${hue} 90% 60%)`, 'dot', 0);
        break;
      }
      case 'trail.fire':
        this.push(x, y, (rng() - 0.5) * 0.8, -0.6 - rng() * 0.6, 26, 4 + rng() * 3, rng() < 0.5 ? '#ff6a1a' : '#ffc43d', 'dot', -0.02);
        break;
      case 'trail.glitch':
        if (rng() < 0.7) {
          this.push(x + (rng() - 0.5) * 12, y + (rng() - 0.5) * 12, 0, 0, 10, 3 + rng() * 4, rng() < 0.5 ? '#39ff14' : '#ff2bd6', 'square', 0);
        }
        break;
      case 'trail.comet':
        this.push(x, y, -vx * 0.1, -vy * 0.1, 40, 3, 'rgba(140,220,255,0.8)', 'dot', 0);
        break;
      case 'trail.tsunami':
        this.push(x, y, (rng() - 0.5) * 1.2, 0.4 + rng() * 0.6, 30, 2.5, 'rgba(80,170,255,0.8)', 'dot', 0.06);
        break;
      default:
        break;
    }
  }

  /** A smoke puff that drifts up and fades: rocket exhaust, explosion aftermath. */
  puff(x: number, y: number, vx: number, vy: number, size: number, rng: () => number, color = '#8d939b', life = 40): void {
    this.push(x + (rng() - 0.5) * 4, y + (rng() - 0.5) * 4, vx + (rng() - 0.5) * 0.6, vy - 0.3 - rng() * 0.4, life * (0.7 + rng() * 0.6), size * (0.7 + rng() * 0.6), color, 'puff', -0.01);
  }

  burst(x: number, y: number, count: number, color: string, speed: number, rng: () => number, life = 30, size = 3): void {
    for (let i = 0; i < count; i++) {
      const a = rng() * Math.PI * 2;
      const v = speed * (0.3 + rng() * 0.7);
      this.push(x, y, Math.cos(a) * v, Math.sin(a) * v - speed * 0.3, life * (0.6 + rng() * 0.6), size * (0.5 + rng()), color, 'dot', 0.12);
    }
  }

  private push(x: number, y: number, vx: number, vy: number, life: number, size: number, color: string, shape: Particle['shape'], gravity: number): void {
    if (this.particles.length > 1500) this.particles.shift();
    this.particles.push({ x, y, vx, vy, life, maxLife: life, size, color, shape, gravity });
  }

  update(): void {
    const next: Particle[] = [];
    for (const p of this.particles) {
      p.life--;
      if (p.life <= 0) continue;
      p.vy += p.gravity;
      p.x += p.vx;
      p.y += p.vy;
      next.push(p);
    }
    this.particles = next;
  }

  draw(ctx: CanvasRenderingContext2D): void {
    for (const p of this.particles) {
      const t = p.life / p.maxLife;
      ctx.globalAlpha = Math.max(0, Math.min(1, t));
      ctx.fillStyle = p.color;
      const sz = p.size * (0.4 + 0.6 * t);
      switch (p.shape) {
        case 'dot':
          ctx.beginPath();
          ctx.arc(p.x, p.y, sz, 0, Math.PI * 2);
          ctx.fill();
          break;
        case 'square':
          ctx.fillRect(p.x - sz, p.y - sz, sz * 2, sz * 2);
          break;
        case 'spark':
          ctx.fillRect(p.x - sz / 2, p.y - sz / 2, sz + Math.abs(p.vx), sz);
          break;
        case 'puff': {
          // Smoke grows as it fades, with a lit top-left.
          const r = p.size * (1.6 - 0.6 * t);
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = 'rgba(255,255,255,0.35)';
          ctx.beginPath();
          ctx.arc(p.x - r * 0.3, p.y - r * 0.3, r * 0.45, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
      }
    }
    ctx.globalAlpha = 1;
  }
}
